import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const ComplianceQuiz = ({ onComplete }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [showResults, setShowResults] = useState(false);

  const questions = [
    {
      id: 1,
      question: "Who reserves the right to amend the Office Policy Manual and when do changes take effect for Associates?",
      options: [
        { value: 'A', label: 'Office Manager; only after each Associate signs acknowledgment' },
        { value: 'B', label: 'Broker; immediately, with review at the next meeting' },
        { value: 'C', label: 'Local Board; after 30 days\' notice' },
        { value: 'D', label: 'State Commission; upon publication' }
      ],
      correct: 'B'
    },
    {
      id: 2,
      question: "Which of the following is NOT one of the Associate's required ongoing obligations?",
      options: [
        { value: 'A', label: 'Maintain an active real estate license' },
        { value: 'B', label: 'Meet CE requirements and provide proof 15 days before renewal' },
        { value: 'C', label: 'Maintain E&O insurance' },
        { value: 'D', label: 'Have the Broker pay all CE and license renewal fees' }
      ],
      correct: 'D'
    },
    {
      id: 3,
      question: "Which statement about automobile use and insurance is correct?",
      options: [
        { value: 'A', label: 'Broker provides vehicles for property tours' },
        { value: 'B', label: 'Associates may use personal vehicles but insurance is optional' },
        { value: 'C', label: 'Associates must carry liability insurance, name the Broker as additional insured, and report coverage changes 15 days in advance' },
        { value: 'D', label: 'Only collision coverage is required' }
      ],
      correct: 'C'
    },
    {
      id: 4,
      question: "Who can authorize termination or amendment of an agency agreement that changes compensation?",
      options: [
        { value: 'A', label: 'Any Associate involved in the transaction' },
        { value: 'B', label: 'The Listing Associate if the seller requests it' },
        { value: 'C', label: 'Only the Broker or authorized manager, in writing' },
        { value: 'D', label: "Seller's attorney" }
      ],
      correct: 'C'
    },
    {
      id: 5,
      question: "Under Fair Housing and the firm's Equal Opportunity policy, which ad copy violates policy?",
      options: [
        { value: 'A', label: '"Master bedroom with walk-in closet"' },
        { value: 'B', label: '"Cozy two-bedroom with family room"' },
        { value: 'C', label: '"Adults only" community' },
        { value: 'D', label: '"Desirable neighborhood" near transit' }
      ],
      correct: 'C'
    },
    {
      id: 6,
      question: "Information Systems use policy states:",
      options: [
        { value: 'A', label: 'Personal use is fully permitted' },
        { value: 'B', label: 'Personal use is prohibited unless the Broker or manager provides written permission' },
        { value: 'C', label: 'Associates may remove data for home storage without approval' },
        { value: 'D', label: 'Associates may alter or delete files at their discretion' }
      ],
      correct: 'B'
    },
    {
      id: 7,
      question: "Which is required when a client/customer appears under the influence of drugs or alcohol?",
      options: [
        { value: 'A', label: 'Proceed with signing but record the impairment' },
        { value: 'B', label: "Terminate that day's activity and reschedule" },
        { value: 'C', label: 'Ask the other party to countersign for them' },
        { value: 'D', label: 'Proceed only if a witness is present' }
      ],
      correct: 'B'
    },
    {
      id: 8,
      question: "Which of the following MUST be reported to the Broker immediately?",
      options: [
        { value: 'A', label: 'Traffic ticket' },
        { value: 'B', label: 'Party default under an accepted contract' },
        { value: 'C', label: "Client's preference for a later closing date" },
        { value: 'D', label: 'A low appraisal' }
      ],
      correct: 'B'
    },
    {
      id: 9,
      question: "Which task MAY an unlicensed assistant perform?",
      options: [
        { value: 'A', label: 'Show listed properties to buyers' },
        { value: 'B', label: 'Negotiate offers with a buyer' },
        { value: 'C', label: 'Assemble documents for closing and schedule appointments for a licensee' },
        { value: 'D', label: 'Discuss contract terms with a seller' }
      ],
      correct: 'C'
    },
    {
      id: 10,
      question: "Under RESPA Section 8 and Regulation X, which is prohibited?",
      options: [
        { value: 'A', label: 'Paying another licensed broker under a written referral agreement' },
        { value: 'B', label: 'Charging for actual settlement services performed' },
        { value: 'C', label: 'Splitting a settlement charge without services rendered' },
        { value: 'D', label: 'Paying an attorney for services actually performed' }
      ],
      correct: 'C'
    },
    {
      id: 11,
      question: "Lead-Based Paint (LBP) rules generally require sellers of target housing to:",
      options: [
        { value: 'A', label: 'Provide a 10-day opportunity for buyers to inspect for LBP (unless mutually altered), give the EPA pamphlet, and disclose known LBP/hazards and records' },
        { value: 'B', label: 'Test and remove LBP before listing' },
        { value: 'C', label: 'Test only if the buyer requests and pays' },
        { value: 'D', label: "Disclose LBP only if the seller's agent is aware" }
      ],
      correct: 'A'
    },
    {
      id: 12,
      question: "Reduction of commissions or fees:",
      options: [
        { value: 'A', label: 'May be offered by the Associate to win the listing' },
        { value: 'B', label: 'Requires written consent from the Broker/manager' },
        { value: 'C', label: 'Is forbidden under all circumstances' },
        { value: 'D', label: 'Is allowed if the seller insists' }
      ],
      correct: 'B'
    },
    {
      id: 13,
      question: "Which is a core antitrust compliance practice?",
      options: [
        { value: 'A', label: 'Discussing "going rates" with competitors at Board events' },
        { value: 'B', label: 'Avoiding any communications that could be construed as agreements on fees' },
        { value: 'C', label: 'Using "For your eyes only-destroy after reading" on sensitive emails' },
        { value: 'D', label: "Agreeing informally to not show a competitor's listings" }
      ],
      correct: 'B'
    },
    {
      id: 14,
      question: "Who is responsible for earnest money disbursement decisions and interpleader filings if there's a dispute?",
      options: [
        { value: 'A', label: 'The Listing Associate' },
        { value: 'B', label: "The Buyer's Attorney" },
        { value: 'C', label: 'The Broker' },
        { value: 'D', label: 'The Title Company' }
      ],
      correct: 'C'
    },
    {
      id: 15,
      question: "Which advertising rule is correct?",
      options: [
        { value: 'A', label: 'Associates may advertise under their name alone if they include a PO Box' },
        { value: 'B', label: 'All advertising must clearly indicate the firm/broker name and comply with supervision and Equal Housing requirements' },
        { value: 'C', label: 'The Equal Housing logo/slogan is optional for print ads' },
        { value: 'D', label: 'Licensees may place signs on any property if they intend to list it' }
      ],
      correct: 'B'
    }
  ];

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubmit = () => {
    // Check if all questions are answered
    const allAnswered = questions.every(q => answers[q.id]);
    
    if (!allAnswered) {
      toast.error('Please answer all questions before submitting');
      return;
    }

    // Calculate score
    let correct = 0;
    questions.forEach(q => {
      if (answers[q.id] === q.correct) {
        correct++;
      }
    });

    const percentage = Math.round((correct / questions.length) * 100);
    setScore(percentage);
    setSubmitted(true);
    setShowResults(true);

    // Pass or fail (80% passing grade)
    const passed = percentage >= 80;
    
    if (passed) {
      toast.success(`Quiz passed with ${percentage}%!`);
      // Call onComplete callback for parent component
      if (onComplete) {
        onComplete({
          score: percentage,
          passed: true,
          answers: answers,
          timestamp: new Date().toISOString()
        });
      }
    } else {
      toast.error(`Score: ${percentage}%. You need 80% to pass. Please review the manual and try again.`);
    }
  };

  const handleRetake = () => {
    setAnswers({});
    setSubmitted(false);
    setShowResults(false);
    setScore(0);
  };

  if (showResults) {
    const passed = score >= 80;
    
    return (
      <Card className="max-w-4xl mx-auto">
        <CardHeader className={`${passed ? 'bg-green-50' : 'bg-red-50'} border-b`}>
          <div className="flex items-center gap-4">
            {passed ? (
              <CheckCircle className="h-12 w-12 text-green-600" />
            ) : (
              <XCircle className="h-12 w-12 text-red-600" />
            )}
            <div>
              <CardTitle className="text-2xl">
                {passed ? 'Congratulations! You Passed!' : 'Quiz Not Passed'}
              </CardTitle>
              <CardDescription className="text-lg mt-2">
                Your Score: {score}% ({questions.filter(q => answers[q.id] === q.correct).length} out of {questions.length} correct)
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {passed ? (
            <div className="space-y-4">
              <p className="text-lg">
                You have successfully completed the Bespoke Realty Office Policies and Procedures Quiz with a passing score of {score}%.
              </p>
              <p className="text-gray-600">
                Your completion has been logged. Please proceed to sign the digital acknowledgment form.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-lg text-red-700">
                A score of 80% or higher is required to pass. You scored {score}%.
              </p>
              <p className="text-gray-600">
                Please review the Office Policies and Procedures Manual and retake the quiz.
              </p>
              <Button onClick={handleRetake} className="bg-[#BCA480] hover:bg-[#a89470]">
                Retake Quiz
              </Button>
            </div>
          )}

          {/* Show incorrect answers */}
          {!passed && (
            <div className="mt-6 border-t pt-6">
              <h3 className="font-semibold text-lg mb-4">Review Incorrect Answers:</h3>
              {questions.map(q => {
                const isCorrect = answers[q.id] === q.correct;
                if (isCorrect) return null;
                
                return (
                  <div key={q.id} className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="font-medium mb-2">Question {q.id}: {q.question}</p>
                    <p className="text-sm text-red-700">
                      Your answer: {answers[q.id]} (Incorrect)
                    </p>
                    <p className="text-sm text-green-700">
                      Correct answer: {q.correct} - {q.options.find(o => o.value === q.correct)?.label}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader className="bg-gradient-to-r from-[#BCA480]/10 to-white">
        <CardTitle className="text-2xl">Office Policies & Procedures Compliance Quiz</CardTitle>
        <CardDescription className="text-base">
          Answer all 15 questions. You must score 80% or higher to pass.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-8">
          {questions.map((question, index) => (
            <div key={question.id} className="pb-6 border-b last:border-b-0">
              <div className="flex items-start gap-3 mb-4">
                <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-[#BCA480] text-white font-bold">
                  {index + 1}
                </span>
                <p className="font-medium text-lg">{question.question}</p>
              </div>
              
              <RadioGroup
                value={answers[question.id] || ''}
                onValueChange={(value) => handleAnswerChange(question.id, value)}
                className="ml-11 space-y-3"
              >
                {question.options.map((option) => (
                  <div key={option.value} className="flex items-start space-x-3">
                    <RadioGroupItem value={option.value} id={`q${question.id}-${option.value}`} />
                    <Label 
                      htmlFor={`q${question.id}-${option.value}`}
                      className="cursor-pointer leading-relaxed"
                    >
                      <span className="font-semibold mr-2">{option.value}.</span>
                      {option.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          ))}
        </div>

        <div className="mt-8 pt-6 border-t flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <AlertCircle className="h-4 w-4" />
            <span>
              {Object.keys(answers).length} of {questions.length} questions answered
            </span>
          </div>
          <Button
            onClick={handleSubmit}
            disabled={submitted}
            className="bg-[#BCA480] hover:bg-[#a89470] px-8"
            size="lg"
          >
            Submit Quiz
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ComplianceQuiz;
